import { create } from "zustand";
import { AutocompleteResult, TripUser } from "@/types";
import { formatInTimeZone } from "date-fns-tz";
import api from "@/config/axiosConfig";
import useAuthStore from "@/stores/user/authStore";
import useTripStore from "@/stores/tripBuilder/useTrip";
import logger from "@/utils/logger";

interface TripCreationState {
  // Form state
  step: number;
  tripName: string;
  leavingFrom: AutocompleteResult | null;
  leavingDate: Date | null;
  autocompleteResults: AutocompleteResult[];
  inputValue: string;
  errorMessage: string;
  isSubmitting: boolean;
  autocompleteLoading: boolean;
  isDatePopoverOpen: boolean;

  // Actions - step navigation
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;

  // Actions - form updates
  setTripName: (name: string) => void;
  setLeavingFrom: (location: AutocompleteResult | null) => void;
  setLeavingDate: (date: Date | null) => void;
  setInputValue: (value: string) => void;
  setDatePopoverOpen: (isOpen: boolean) => void;
  setErrorMessage: (message: string) => void;

  // Actions - autocomplete
  handleAutocompleteSelect: (result: AutocompleteResult) => void;

  // Action - submission
  createTrip: () => Promise<string | null>;
  reset: () => void;
}

const initialState = {
  step: 1,
  tripName: "",
  leavingFrom: null,
  leavingDate: null,
  autocompleteResults: [],
  inputValue: "",
  errorMessage: "",
  isSubmitting: false,
  autocompleteLoading: false,
  isDatePopoverOpen: false,
};

const useTripCreation = create<TripCreationState>((set, get) => ({
  ...initialState,

  // Step navigation
  nextStep: () => {
    const { step, tripName, leavingFrom, inputValue } = get();

    // Validation before proceeding
    if (step === 1 && tripName.trim() === "") {
      logger.log("Please enter a trip name to proceed.");
      set({ errorMessage: "Please enter a trip name to proceed." });
      return;
    }

    if (step === 2 && leavingFrom === null && inputValue.trim() === "") {
      logger.log("Please select a departure location to proceed.");
      set({ errorMessage: "Please select a departure location to proceed." });
      return;
    }

    set({ step: step + 1, errorMessage: "" });
  },

  prevStep: () => {
    const { step } = get();
    if (step > 1) {
      set({ step: step - 1, errorMessage: "" });
    }
  },

  goToStep: (step: number) => {
    set({ step, errorMessage: "" });
  },

  // Form updates
  setTripName: (name: string) => set({ tripName: name }),
  setLeavingFrom: (location: AutocompleteResult | null) =>
    set({ leavingFrom: location }),
  setLeavingDate: (date: Date | null) => {
    if (date) {
      // Convert to UTC
      const utcDate = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      );
      set({ leavingDate: utcDate, isDatePopoverOpen: false });
      // Automatically move to next step
      const { step } = get();
      set({ step: step + 1 });
    } else {
      set({ leavingDate: null });
    }
  },
  setInputValue: (value: string) => set({ inputValue: value }),
  setDatePopoverOpen: (isOpen: boolean) => set({ isDatePopoverOpen: isOpen }),
  setErrorMessage: (message: string) => set({ errorMessage: message }),

  handleAutocompleteSelect: (result: AutocompleteResult) => {
    set({
      leavingFrom: result,
      inputValue: result.name,
      autocompleteResults: [],
    });
    get().nextStep();
  },

  // Submission
  createTrip: async () => {
    const { tripName, leavingFrom, leavingDate } = get();

    if (!leavingDate) {
      set({ errorMessage: "Please select a departure date to proceed." });
      return null;
    }

    set({ isSubmitting: true, errorMessage: "" });

    try {
      const tripId = crypto.randomUUID();
      const currentUser = useAuthStore.getState().user;

      const response = await api.trips.post(`/${tripId}`, {
        id: tripId,
        name: tripName,
        origin: leavingFrom,
        date_departure: formatInTimeZone(
          leavingDate,
          "UTC",
          "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
        ),
        user: {
          user_id: currentUser?.user_id,
          first_name: currentUser?.first_name,
          last_name: currentUser?.last_name,
          name: currentUser?.name,
          username: currentUser?.username,
          profile_image: currentUser?.profile_image,
          is_admin: true,
          creator: true,
        } as TripUser,
      });

      // Reset the trip store for the new trip
      useTripStore.setState({
        tripData: null,
        localVersion: 0,
        selectedLocationId: null,
      });

      set({ isSubmitting: false });
      return tripId;
    } catch (error) {
      console.error("Error creating trip:", error);
      set({
        isSubmitting: false,
        errorMessage: "Failed to create trip. Please try again.",
      });
      return null;
    }
  },

  // Reset the store to initial state
  reset: () => set(initialState),
}));

export default useTripCreation;
